import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchLeads, fetchWaitlist } from "@/lib/api";

const fmtDate = (iso) => new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });

export default function Inbox() {
  const [key, setKey] = useState(sessionStorage.getItem("axs_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const [l, w] = await Promise.all([fetchLeads(k), fetchWaitlist(k)]);
      setLeads(l.data);
      setWaitlist(w.data);
      setAuthed(true);
      sessionStorage.setItem("axs_admin_key", k);
    } catch {
      setError("ACCESS DENIED — invalid key");
      setAuthed(false);
      sessionStorage.removeItem("axs_admin_key");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    sessionStorage.removeItem("axs_admin_key");
    setKey("");
    setAuthed(false);
    setLeads([]);
    setWaitlist([]);
  };

  return (
    <div className="min-h-screen bg-[#050505] font-mono text-white antialiased" data-testid="inbox-page">
      <div className="noise-overlay" />
      <header className="border-b border-white/10 px-6 py-4 lg:px-12">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between">
          <Link to="/" data-testid="inbox-back-link" className="font-display text-lg font-extrabold tracking-tighter">
            AXIOSERA<span className="text-[#00FF41]"> / INBOX</span>
          </Link>
          {authed && (
            <div className="flex gap-3">
              <button data-testid="inbox-refresh-button" onClick={() => load(key)} className="border border-white/20 px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:border-[#00FF41] hover:text-[#00FF41]">
                Refresh
              </button>
              <button data-testid="inbox-logout-button" onClick={logout} className="border border-white/20 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-300 hover:border-[#FF3B30] hover:text-[#FF3B30]">
                Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {!authed ? (
        <div className="mx-auto max-w-md px-6 pt-32" data-testid="inbox-gate">
          <div className="scanlines border border-white/10 bg-[#0A0A0B]">
            <div className="border-b border-white/10 px-6 py-3 text-[11px] uppercase tracking-[0.25em] text-zinc-500">
              restricted · lead inbox
            </div>
            <form
              data-testid="inbox-auth-form"
              onSubmit={(e) => {
                e.preventDefault();
                load(key);
              }}
              className="p-6"
            >
              <label className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">Access key</label>
              <input
                type="password"
                required
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="axs-••••••••••••"
                data-testid="inbox-key-input"
                className="mt-3 w-full border border-white/10 bg-[#0F0F11] px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-300 focus:border-[#00FF41]"
              />
              {error && (
                <div data-testid="inbox-access-denied" className="mt-4 border border-[#FF3B30]/40 px-4 py-2 text-xs text-[#FF3B30]">
                  &gt; {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="inbox-auth-button"
                className="mt-6 w-full bg-[#00FF41] px-6 py-3 text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white disabled:opacity-50"
              >
                {loading ? "Authenticating..." : "Authenticate →"}
              </button>
            </form>
          </div>
        </div>
      ) : (
        <main className="mx-auto max-w-[1400px] px-6 py-12 lg:px-12" data-testid="inbox-dashboard">
          <div className="mb-10 flex flex-wrap gap-x-10 gap-y-2 text-[11px] uppercase tracking-[0.25em] text-zinc-500">
            <span><span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-[#00FF41] glow-dot" />SYS: ONLINE</span>
            <span>PILOT APPLICATIONS: <span className="text-[#00FF41]">{leads.length}</span></span>
            <span>WAITLIST: <span className="text-[#00FF41]">{waitlist.length}</span></span>
          </div>

          <h1 className="font-display text-3xl font-extrabold tracking-tighter" data-testid="inbox-leads-heading">
            Design-partner applications
          </h1>
          <div className="mt-6 overflow-x-auto border border-white/10" data-testid="leads-table">
            <table className="w-full min-w-[900px] text-left text-xs">
              <thead>
                <tr className="border-b border-white/10 text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                  {["Submitted", "Name", "Email", "Company", "Revenue", "Price points", "Message"].map((h) => (
                    <th key={h} className="px-4 py-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-zinc-600">No applications yet.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 transition-colors duration-300 hover:bg-[#0F0F11]" data-testid={`lead-row-${l.id.slice(0, 8)}`}>
                    <td className="px-4 py-3 text-zinc-500">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3">{l.name}</td>
                    <td className="px-4 py-3 text-[#00FF41]">{l.email}</td>
                    <td className="px-4 py-3">{l.company}</td>
                    <td className="px-4 py-3 text-zinc-400">{l.revenue_band || "—"}</td>
                    <td className="px-4 py-3 text-zinc-400">{l.price_points || "—"}</td>
                    <td className="max-w-[240px] truncate px-4 py-3 text-zinc-500">{l.message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="mt-16 font-display text-3xl font-extrabold tracking-tighter" data-testid="inbox-waitlist-heading">
            Waitlist
          </h2>
          <div className="mt-6 overflow-x-auto border border-white/10" data-testid="waitlist-table">
            <table className="w-full min-w-[500px] text-left text-xs">
              <thead>
                <tr className="border-b border-white/10 text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                  <th className="px-4 py-3 font-medium">Joined</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                </tr>
              </thead>
              <tbody>
                {waitlist.length === 0 && (
                  <tr><td colSpan={2} className="px-4 py-8 text-zinc-600">No signups yet.</td></tr>
                )}
                {waitlist.map((w) => (
                  <tr key={w.id} className="border-b border-white/5 transition-colors duration-300 hover:bg-[#0F0F11]" data-testid={`waitlist-row-${w.id.slice(0, 8)}`}>
                    <td className="px-4 py-3 text-zinc-500">{fmtDate(w.created_at)}</td>
                    <td className="px-4 py-3 text-[#00FF41]">{w.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </main>
      )}
    </div>
  );
}
